import Axios, { AxiosError } from "axios";
import { isTokenPastExpiry } from "../../oauth/token";
import { SESSION_ENDED, signalSessionEnded } from "./session-ended";

/**
 * Swallow the rejection: the dialog owns what happens next, and letting the
 * error propagate would show a page-level failure behind the modal.
 */
const neverSettles = () => new Promise<never>(() => undefined);

Axios.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.resolve(error);
  }
);

const redirect = () => {
  localStorage.removeItem("token");
  window.location.href = "/";
};

Axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (err: AxiosError) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      // This interceptor is the FIRST thing to see a dead session in practice
      // — data calls happen constantly, while the heartbeat is every 5s and a
      // refresh far rarer. So it has to classify, not just redirect.
      //
      // 401 + code  = the portal server says the session was ended.
      // bare 403    = its guard rejected the token; for a logged-in user that
      //               means expiry (Nest turns a guard returning false into
      //               ForbiddenException, with no code in the body).
      const code = (err.response?.data as { code?: string } | undefined)?.code;
      if (code === SESSION_ENDED) {
        signalSessionEnded("displaced");
        return neverSettles();
      }
      if (isTokenPastExpiry(localStorage.getItem("token"))) {
        signalSessionEnded("expired");
        return neverSettles();
      }
      // A genuine authorisation failure is not a session problem — the old
      // silent redirect is still right for it.
      redirect();
      return;
    }
    return Promise.reject(err);
  }
);

export default Axios;
